import { clearPinnedSkillRuns } from './stores/pinned-skill-runs-store';
import { clearDismissedSkillRuns } from './stores/dismissed-skill-runs-store';
import { clearDetachedBgChats } from './stores/detached-bg-chats-store';
import { clearAllCanvasStorage } from '~/features/canvas/canvas-storage';

/**
 * FRONT-732: purge per-account client state when the signed-in user changes.
 *
 * The Inspector's finished/kept cards (pinned / dismissed / detached-bg-chat)
 * and the chat window's Canvas state (per-chat panel view state and per-item
 * unsaved document DRAFTS, `eddy.canvas.*`; a draft is a person's own words about
 * their own document) are persisted in localStorage under global keys. On a shared browser, logging
 * out and back in as a different account left the previous user's cards behind —
 * and clicking one fetched a run the new account can't access, erroring.
 *
 * We stamp the localStorage with the current user id and, whenever it differs
 * from what we saw last, drop those stores. Keyed on the *authenticated user id*
 * (not the logout button) so it also catches a silent session-expiry → different
 * login, where the logout handler never runs. A same-user re-login keeps its
 * cards (the id matches → no purge).
 */
const ACCOUNT_KEY = 'eddy.account-id';

function purge(): void {
  clearPinnedSkillRuns();
  clearDismissedSkillRuns();
  clearDetachedBgChats();
  clearAllCanvasStorage();
}

export function reconcileAccountScope(userId: string | null | undefined): void {
  if (!userId) return;
  let prev: string | null = null;
  try {
    prev = localStorage.getItem(ACCOUNT_KEY);
  } catch {
    // Storage unreadable — we can't confirm who the current cards belong to, so
    // fail safe and purge rather than risk showing the prior user's cards
    // (the in-memory stores may still hold them across an SPA session switch).
    purge();
    return;
  }
  if (prev === userId) return;

  purge();

  try {
    localStorage.setItem(ACCOUNT_KEY, userId);
  } catch {
    /* best-effort stamp */
  }
}
